import React, { useEffect, useRef } from 'react';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import Point from '@arcgis/core/geometry/Point';
import esriConfig from '@arcgis/core/config';
import '@arcgis/core/assets/esri/themes/light/main.css';

// Whole Farm bids carry no parcel geometry of their own, so they're plotted as a
// dot at the farmer's first field instead (see fieldLocation, computed server-side
// in admin.js). One farmer can hold several bids/actions at that same location,
// each with its own independent decisionStatus — coloring the dot by any single
// bid's status would misrepresent the others, so it's always plain teal and
// carries no status meaning (the per-action status lives in the bid list instead).
const WHOLE_FARM_DOT_SYMBOL = { color: [13, 148, 136, 0.85], outline: { color: [255, 255, 255], width: 1.5 } };

// Whole Farm dots are drawn in fixed screen pixels, so at a country-wide zoom
// a 14px dot can visually swamp the map — scale it down as the view zooms out
// and back up near the ~1:10000 magnifying-glass target (zoom ~16).
const getDotSize = (zoom) => Math.max(4, Math.min(16, Math.round(zoom || 6)));

// Shared between field polygons and Whole Farm dots so both shapes use identical
// status colors — Pending/Winning/Losing must mean the same thing regardless of
// which geometry a bid happens to render as.
const getStatusColors = (status) => {
  if (status === 'accepted') {
    return { fillColor: [255, 255, 255, 0.8], outlineColor: [245, 158, 11, 1], outlineWidth: 2 };   // Winning
  } else if (status === 'rejected') {
    return { fillColor: [71, 85, 105, 0.7], outlineColor: [71, 85, 105, 0], outlineWidth: 0 };       // Losing
  } else if (status === 'withdrawn') {
    return { fillColor: [30, 41, 59, 0.8], outlineColor: [30, 41, 59, 0], outlineWidth: 0 };         // Withdrawn (post-closure winner pulled)
  }
  return { fillColor: [245, 158, 11, 0.5], outlineColor: [245, 158, 11, 1], outlineWidth: 2 };         // Pending
};

const getStatusLabel = (status) => {
    switch(status) {
        case 'accepted': return 'Winning Bid';
        case 'rejected': return 'Losing Bid';
        case 'pending':  return 'Bid Submitted';
        case 'draft':    return 'Draft';
        case 'withdrawn': return 'Withdrawn';
        default:         return status ? (status.charAt(0).toUpperCase() + status.slice(1)) : 'Unknown';
    }
};

const AuctionMap = ({ bids = [], focusParcel = null, zoomTarget = null, isManual = false, onReady = null, instantZoom = false }) => {
  const mapDiv = useRef(null);
  const viewRef = useRef(null);
  const layersRef = useRef({ bidLayer: null });
  // Guards the auto-zoom effect below against React dev-mode's double-effect
  // invocation (this app runs <React.StrictMode> — see index.js): with no
  // cleanup function, React calls this effect's body twice in a row on
  // mount, so without this guard TWO independent goTo()+scale-cap chains can
  // race, and whichever settles/calls onReady first wins the print
  // screenshot — regardless of which one (if either) actually finished
  // applying the scale cap. Bumped at the top of every invocation; only the
  // invocation whose captured value still matches by the time its async work
  // resolves is allowed to touch the view or call onReady.
  const zoomGenRef = useRef(0);

  // 1. INITIALIZE MAP (Runs Once)
  useEffect(() => {
    const apiKey = process.env.REACT_APP_ARCGIS_API_KEY_AGOL_API_SECRET;
    if (apiKey) esriConfig.apiKey = apiKey;

    // Diagnostic only for the print path — three straight guesses at the
    // goTo() crash (target shape, animate option, GPU/WebGL Chrome flags)
    // all failed to change the identical error, so this checks the one thing
    // none of those guesses actually confirmed: whether this page can get a
    // real WebGL context at all. See [MapSnapshot] logs in the server console.
    if (instantZoom) {
      const canvas = document.createElement('canvas');
      const gl2 = canvas.getContext('webgl2');
      const gl1 = !gl2 && canvas.getContext('webgl');
      // JSON.stringify, not a raw object — Puppeteer's console forwarding
      // (see contractBuilder.js's captureMapSnapshot) stringifies console
      // args via msg.text(), which renders a plain object as the useless
      // literal string "[object Object]". Every earlier structured log in
      // this file had this same bug — only the goTo error's .stack survived
      // because it was already a string, which is the only reason that
      // diagnostic worked at all.
      console.log('[AuctionMap] WebGL availability check', JSON.stringify({
        webgl2: !!gl2, webgl1: !!gl1,
        webgl2Version: gl2 ? gl2.getParameter(gl2.VERSION) : null,
      }));
    }

    if (!mapDiv.current) return;

    const map = new Map({ basemap: "topo-vector" });

    // Create ONLY the Bid Layer
    const bidLayer = new GraphicsLayer({ title: "Bids" });
    map.add(bidLayer);
    
    // Store refs for the Update Effect
    layersRef.current = { bidLayer };

    const view = new MapView({
      container: mapDiv.current,
      map: map,
      center: [-2.5, 54.5], // Default UK center
      zoom: 6,
      ui: { components: ["zoom", "attribution"] }
    });

    viewRef.current = view;

    return () => {
      if (viewRef.current) viewRef.current.destroy();
    };
    // This effect runs once on mount by design (creates the view); instantZoom
    // never changes across this component's lifetime for either caller
    // (AdminBidDetails/BidMapSnapshot each pass it as a fixed literal), so
    // omitting it from deps doesn't change behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. UPDATE BIDS & AUTO-ZOOM (Runs on Data Change)
  useEffect(() => {
    zoomGenRef.current += 1;
    const myZoomGen = zoomGenRef.current;
    const isStaleZoom = () => zoomGenRef.current !== myZoomGen;

    const { bidLayer } = layersRef.current;
    if (!bidLayer) return;

    // --- DRAW BIDS ---
    bidLayer.removeAll();

    let bidGraphics;

    if (isManual) {
      // Whole Farm mode: dedupe to one dot per farmer (not per bid) — a farmer
      // with 3 actions bid at the same location would otherwise stack 3 identical
      // overlapping dots. Uses a plain object rather than the JS Map collection,
      // since the `Map` identifier in this file is the ArcGIS Map class (imported
      // above) and shadows the global — `new Map()` here would silently construct
      // an ArcGIS web map instead, which has no .set/.has/.values.
      const seenFarmerIds = new Set();
      const dedupedFarmerBids = [];
      bids.forEach(bid => {
        const farmerId = bid.user?._id || bid.user?.email;
        if (!bid.fieldLocation || !farmerId || seenFarmerIds.has(farmerId)) return;
        seenFarmerIds.add(farmerId);
        dedupedFarmerBids.push(bid);
      });

      bidGraphics = dedupedFarmerBids.map(bid => new Graphic({
        geometry: new Point({ longitude: bid.fieldLocation.lng, latitude: bid.fieldLocation.lat, spatialReference: { wkid: 4326 } }),
        symbol: { type: "simple-marker", style: "circle", size: getDotSize(viewRef.current?.zoom), color: WHOLE_FARM_DOT_SYMBOL.color, outline: WHOLE_FARM_DOT_SYMBOL.outline },
        popupTemplate: {
          title: "Whole Farm Bid Location",
          content: `<b>Farmer:</b> {farmer}`
        },
        attributes: {
          farmer: bid.user?.name || 'Unknown'
        }
      }));
    } else {
      const fieldGraphics = bids.map(bid => {
        if (!bid.parcel || !bid.parcel.geometry) return null;

        let geo = bid.parcel.geometry;
        let rings = geo.coordinates || geo;
        if (geo.type === 'MultiPolygon' && Array.isArray(rings)) rings = rings.flat(1);
        if (!Array.isArray(rings) || rings.length === 0) return null;

        const { fillColor, outlineColor, outlineWidth } = getStatusColors(bid.status);

        return new Graphic({
          geometry: new Polygon({ rings: rings, spatialReference: { wkid: 4326 } }),
          symbol: { type: "simple-fill", color: fillColor, outline: { color: outlineColor, width: outlineWidth } },
          popupTemplate: {
            title: "Bid: {ref}",
            content: `<b>Farmer:</b> {farmer}<br><b>Price:</b> £{amount}<br><b>Status:</b> {status}`
          },
          attributes: {
            ref: bid.parcel.parcelRef || 'Unknown',
            farmer: bid.user?.name || 'Unknown',
            amount: bid.amount,
            status: getStatusLabel(bid.status)
          }
        });
      }).filter(Boolean);

      // Non-manual Whole Farm bids (actionType: whole_farm_non_manual) carry no
      // parcel geometry either — same as Manual Review bids — so they're plotted
      // at the farmer's fieldLocation too. Unlike Manual Review's one-dot-per-farmer
      // dedup (where a single admin decision covers several actions at once), these
      // bids are individually ranked/scored, so each bid gets its own dot colored
      // by that bid's own status, matching the field-polygon color scheme.
      const wholeFarmGraphics = bids.map(bid => {
        if (bid.parcel || !bid.fieldLocation) return null;

        const { fillColor, outlineColor, outlineWidth } = getStatusColors(bid.status);

        return new Graphic({
          geometry: new Point({ longitude: bid.fieldLocation.lng, latitude: bid.fieldLocation.lat, spatialReference: { wkid: 4326 } }),
          symbol: { type: "simple-marker", style: "circle", size: getDotSize(viewRef.current?.zoom), color: fillColor, outline: { color: outlineColor, width: outlineWidth || 1 } },
          popupTemplate: {
            title: "Whole Farm Bid",
            content: `<b>Farmer:</b> {farmer}<br><b>Price:</b> £{amount}<br><b>Status:</b> {status}`
          },
          attributes: {
            farmer: bid.user?.name || 'Unknown',
            amount: bid.amount,
            status: getStatusLabel(bid.status)
          }
        });
      }).filter(Boolean);

      bidGraphics = [...fieldGraphics, ...wholeFarmGraphics];
    }

    bidLayer.addMany(bidGraphics);

    // 👇 AUTO-ZOOM TO BIDS
    if (bidGraphics.length > 0 && viewRef.current) {
        const view = viewRef.current;

        const signalReady = () => {
            // The one authoritative gate: no matter how many stale/racing
            // invocations of this effect got this far, only the invocation
            // that's still current when its async chain finishes is allowed
            // to call onReady — otherwise a stale invocation's completion can
            // fire the print-ready marker (or overwrite the view again after
            // the real one already settled) purely based on which of two
            // racing goTo chains happens to resolve first.
            if (isStaleZoom()) return;
            if (instantZoom) {
                const v = viewRef.current;
                // JSON.stringify — see the WebGL check above for why a raw
                // object here would just log as "[object Object]" over the
                // Puppeteer console bridge.
                console.log('[AuctionMap] print snapshot zoom settled', JSON.stringify({
                    graphicsCount: bidGraphics.length,
                    zoom: v?.zoom, scale: v?.scale,
                    center: v?.center ? [v.center.longitude, v.center.latitude] : null,
                }));
            }
            onReady?.();
        };

        // Print snapshots (contract packs) shouldn't zoom in tighter than a
        // single small field would naturally fit to — 1:10,000 matches the
        // existing convention elsewhere in this file (the magnifying-glass
        // zoomTarget effect below uses the same fixed scale for Whole Farm
        // dots). Not applied to the interactive admin map, which has no such
        // requirement.
        const MIN_PRINT_SCALE = 10000;

        const finishUp = () => {
            // goTo's promise resolves once the view's state has updated,
            // which can land a frame or two ahead of the canvas actually
            // compositing that state — double rAF waits for two real
            // paint cycles before Puppeteer is told it's safe to
            // screenshot, for the print path only (the interactive admin
            // map has no screenshot timing to worry about).
            if (instantZoom) {
                requestAnimationFrame(() => requestAnimationFrame(signalReady));
            } else {
                signalReady();
            }
        };

        const runGoTo = () => {
            // A stale invocation reaching here (view.when() resolved after a
            // newer invocation of this effect already started) has nothing
            // useful left to do — the newer one owns the view from here.
            if (isStaleZoom()) return;
            // Adding a slight padding so the shapes aren't pressed exactly against the map edges.
            // goTo() can throw synchronously on an invalid target (not just reject), so this is
            // wrapped in try/catch as well as the usual promise .catch().
            try {
                view.goTo(bidGraphics, { padding: 50 })
                    .catch(err => {
                        if (err.name !== "AbortError") console.error("Auto-zoom failed", err?.stack || err);
                    })
                    .finally(() => {
                        if (isStaleZoom()) return;
                        if (instantZoom && view.scale < MIN_PRINT_SCALE) {
                            view.goTo({ target: view.center, scale: MIN_PRINT_SCALE })
                                .catch(err => {
                                    if (err.name !== "AbortError") console.error("Scale-cap goTo failed", err?.stack || err);
                                })
                                .finally(finishUp);
                        } else {
                            finishUp();
                        }
                    });
            } catch (err) {
                console.error("Auto-zoom failed synchronously", err?.stack || err);
                if (!isStaleZoom()) onReady?.();
            }
        };

        // Root cause (confirmed via a real stack trace — three prior guesses at
        // target shape / animate option / GPU-WebGL flags all missed this):
        // goTo() was being called before the freshly-constructed MapView had
        // actually finished loading, crashing deep inside its internal
        // `animation` property setter ("Cannot read properties of undefined
        // (reading 'animation')", `f.goTo -> f.set [as animation] -> m.set`).
        // AdminBidDetails' live map never hits this because its `bids` prop
        // normally arrives after an async fetch, well after the view has had
        // time to finish loading on its own — BidMapSnapshot is unusual in that
        // `bids` is already populated on the very first render, so the
        // view-creation effect and this auto-zoom effect fire back-to-back with
        // no gap. `view.when()` is the standard ArcGIS pattern for "wait until
        // the view has actually finished initializing" — never optional, and
        // its absence here (not the target shape, not `animate`, not the GPU
        // flags) was the whole bug.
        view.when(runGoTo, (err) => {
            console.error("View failed to load before auto-zoom", err?.stack || err);
            if (!isStaleZoom()) onReady?.();
        });
    } else {
        if (instantZoom) console.log('[AuctionMap] print snapshot: no graphics to zoom to (bidGraphics empty)');
        // Nothing to draw/zoom to — still ready (e.g. print-only snapshot
        // consumers like BidMapSnapshot.js need this signal either way).
        onReady?.();
    }

  }, [bids, isManual, onReady, instantZoom]);

  // 3. MANUAL ZOOM HANDLER (Runs when admin clicks the magnifying glass)
  useEffect(() => {
      if (focusParcel && viewRef.current) {
          let rings = focusParcel.coordinates || focusParcel;
          if (focusParcel.type === 'MultiPolygon') rings = rings.flat(1);

          const polygon = new Polygon({
              rings: rings,
              spatialReference: { wkid: 4326 }
          });

          try {
              viewRef.current.goTo(polygon).catch(err => {
                  if (err.name !== "AbortError") console.error("Zoom failed", err);
              });
          } catch (err) {
              console.error("Zoom failed synchronously", err);
          }
      }
  }, [focusParcel]);

  // 4. MAGNIFYING GLASS ZOOM (Whole Farm bid list — fixed ~1:10000 scale on a point,
  // rather than fitting to a polygon's extent like the effect above)
  useEffect(() => {
      if (zoomTarget && viewRef.current) {
          try {
              viewRef.current.goTo(
                  { target: new Point({ longitude: zoomTarget.lng, latitude: zoomTarget.lat, spatialReference: { wkid: 4326 } }), scale: 10000 },
                  { duration: 600 }
              ).catch(err => {
                  if (err.name !== "AbortError") console.error("Zoom to field failed", err);
              });
          } catch (err) {
              console.error("Zoom to field failed synchronously", err);
          }
      }
  }, [zoomTarget]);

  // 5. LIVE DOT RESIZE (Whole Farm dots shrink/grow with zoom, in-place — no bids reload)
  useEffect(() => {
      const view = viewRef.current;
      if (!view) return;
      const handle = view.watch('zoom', (newZoom) => {
          const { bidLayer } = layersRef.current;
          if (!bidLayer) return;
          const size = getDotSize(newZoom);
          bidLayer.graphics.forEach(g => {
              if (g.symbol?.type === 'simple-marker') {
                  const resized = g.symbol.clone();
                  resized.size = size;
                  g.symbol = resized;
              }
          });
      });
      return () => handle.remove();
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '400px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd', display: 'flex', flexDirection: 'column' }}>
      <div ref={mapDiv} style={{ flex: 1, width: '100%' }} />
      {!isManual && (
        <div style={styles.legend}>
          <div style={styles.legendTitle}>Bid Status</div>
          <div style={styles.legendItem}><span style={{...styles.dot, backgroundColor: 'rgba(245, 158, 11, 0.5)', border: '2px solid #F59E0B'}}></span> Pending decision</div>
          <div style={styles.legendItem}><span style={{...styles.dot, backgroundColor: '#FFFFFF', border: '2px solid #F59E0B'}}></span> Winning Bids</div>
          <div style={styles.legendItem}><span style={{...styles.dot, backgroundColor: '#475569'}}></span> Losing Bids</div>
          <div style={styles.legendItem}><span style={{...styles.dot, backgroundColor: 'rgba(13, 148, 136, 0.85)'}}></span> Explore</div>
          <div style={styles.legendItem}><span style={{...styles.dot, backgroundColor: '#1E293B'}}></span> Withdrawn</div>
        </div>
      )}
    </div>
  );
};

const styles = {
  legend: { position: 'absolute', bottom: '20px', left: '20px', backgroundColor: '#FFFFFF', padding: '10px', borderRadius: '4px', boxShadow: '0 2px 5px rgba(0,0,0,0.3)', fontSize: '0.85rem', fontFamily: '"Inter", sans-serif', color: '#0F172A' },
  legendTitle: { fontWeight: 'bold', fontFamily: '"Plus Jakarta Sans", sans-serif', marginBottom: '6px', fontSize: '0.85rem', color: '#0F172A' },
  legendItem: { display: 'flex', alignItems: 'center', marginBottom: '5px' },
  dot: { width: '12px', height: '12px', borderRadius: '50%', marginRight: '8px' }
};

export default AuctionMap;